/* eslint-disable unicorn/no-lonely-if */
import { useAction, useAtom } from '@reatom/npm-react'
import { AnimatePresence, motion } from 'motion/react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useCounter } from 'react-use'
import styled from 'styled-components'

import { Duration } from '@blade/std-lib'
import { Tooltip } from '@blade/ui-kit'
import { ZeroSlot } from '@root/entities/network/model/solana'
import { ZeroSlotApiKey } from '@root/entities/trade-settings/model'
import { solRpcSettingsModelByPreset } from '@root/features/trade/trade-settings/atoms'
import { ControlsQuickOption } from '@root/features/trade/trading-controls/ui/common'
import { useTranslation } from '@root/shared/localization'
import { GlobeIcon, HeliusIcon, Input, ZeroSlotIcon } from '@root/shared/ui-kit'

import { ExpandableSelectCard } from './common/expandable-select-card'

import type { SolRpcSettingsModel } from '@root/features/trade/trade-settings/atoms'

const FLASH_DURATION = Duration.seconds(1)

export const TradeSettingsFormSolRpc: React.FC<{ preset: number }> = ({
  preset,
}) => {
  const model = useMemo(() => {
    return solRpcSettingsModelByPreset[preset]
  }, [preset])

  if (!model) {
    return undefined
  }

  return <TradeSettingsFormSolRpc_ model={model} />
}

const TradeSettingsFormSolRpc_ = ({
  model,
}: {
  model: SolRpcSettingsModel
}) => {
  const { t } = useTranslation()

  const [selectedRpcType] = useAtom(model.selectedRpcType)
  const [customRpcUrl] = useAtom(model.customRpcUrl)
  const [zeroSlotApiKey] = useAtom(model.zeroSlotApiKey)
  const [zeroSlotRegion] = useAtom(model.zeroSlotRegion)

  const setSelectedRpcType = useAction(model.setSelectedRpcType)
  const setCustomRpcUrl = useAction(model.setCustomRpcUrl)
  const setZeroSlotApiKey = useAction(model.setZeroSlotApiKey)
  const setZeroSlotRegion = useAction(model.setZeroSlotRegion)

  const [selectedRpcTypeInternal, setSelectedRpcTypeInternal] =
    useState(selectedRpcType)
  const [customRpcRaw, setCustomRpcRaw] = useState(customRpcUrl)
  const [zeroSlotApiKeyRaw, setZeroSlotApiKeyRaw] =
    useState<string>(zeroSlotApiKey)
  const [showSaved, { reset: resetShowSaved, inc: setShowSaved }] =
    useCounter(0)

  const customRpcValid = useMemo(
    () => validateRpcUrl(customRpcRaw),
    [customRpcRaw],
  )

  const apiKeyValid = useMemo(
    () => zeroSlotApiKeyRaw.length > 0,
    [zeroSlotApiKeyRaw],
  )

  useEffect(() => {
    if (showSaved) {
      const timeout = setTimeout(
        () => resetShowSaved(),
        Duration.toMillis(FLASH_DURATION),
      )
      return () => clearTimeout(timeout)
    }
  }, [resetShowSaved, showSaved])

  const handleCustomRpcSave = () => {
    if (customRpcValid) {
      setSelectedRpcType('custom')
      setCustomRpcUrl(customRpcRaw)
      setShowSaved()
    }
  }

  const handleZeroSlotApiKeySave = useCallback(() => {
    if (apiKeyValid) {
      setSelectedRpcType('0slot')
      setZeroSlotApiKey(ZeroSlotApiKey(zeroSlotApiKeyRaw))
      setShowSaved()
    }
  }, [
    apiKeyValid,
    setSelectedRpcType,
    setZeroSlotApiKey,
    zeroSlotApiKeyRaw,
    setShowSaved,
  ])

  return (
    <>
      <Header>
        <Tooltip content={t('trade-settings.execution-route-tooltip')}>
          <Label>{t('trade-settings.execution-route')}</Label>
        </Tooltip>
        <AnimatePresence>
          {showSaved ? <SavedBadge>{t('common.saved')}</SavedBadge> : undefined}
        </AnimatePresence>
      </Header>

      <Routes>
        <ExpandableSelectCard
          selected={selectedRpcTypeInternal === 'helius-sender'}
          onSelect={() => {
            setSelectedRpcTypeInternal('helius-sender')
            setSelectedRpcType('helius-sender')
            setShowSaved()
          }}
          icon={HeliusIcon}
          title={t('trade-settings.helius-sender')}
          description={t('trade-settings.helius-sender-description')}
          toolTipMessage={t('trade-settings.helius-sender-tooltip')}
        />

        <ExpandableSelectCard
          selected={selectedRpcTypeInternal === '0slot'}
          onSelect={() => {
            setSelectedRpcTypeInternal('0slot')
            handleZeroSlotApiKeySave()
          }}
          icon={ZeroSlotIcon}
          title="0slot"
          description={t('trade-settings.zero-slot-description')}
          toolTipMessage={t('trade-settings.zero-slot-tooltip')}
        >
          <SubLabel>{t('trade-settings.location')}</SubLabel>
          <OptionsGrid>
            {ZeroSlot.regions.map((r) => (
              <ControlsQuickOption
                key={r.id}
                title={r.label}
                active={zeroSlotRegion === r.id}
                onClick={() => setZeroSlotRegion(r.id)}
              />
            ))}
          </OptionsGrid>
          <SubLabel>{t('trade-settings.api-key')}</SubLabel>
          <Input
            value={zeroSlotApiKeyRaw}
            onChange={(e) => setZeroSlotApiKeyRaw(e.target.value)}
            placeholder={t('trade-settings.zero-slot.api-key-placeholder')}
            autoCorrect="false"
            autoComplete="false"
            autoCapitalize="false"
            error={zeroSlotApiKeyRaw.length === 0}
            onBlur={(e) => {
              if (e.relatedTarget instanceof Node) {
                if (!e.relatedTarget.contains(e.target)) {
                  return
                }
              }
              handleZeroSlotApiKeySave()
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                e.stopPropagation()
                handleZeroSlotApiKeySave()
              }
            }}
          />
        </ExpandableSelectCard>

        <ExpandableSelectCard
          selected={selectedRpcTypeInternal === 'custom'}
          onSelect={() => {
            setSelectedRpcTypeInternal('custom')
            handleCustomRpcSave()
          }}
          icon={GlobeIcon}
          title={t('trade-settings.custom-rpc')}
          description={t('trade-settings.custom-rpc-description')}
        >
          <SubLabel>{t('trade-settings.rpc-endpoint')}</SubLabel>
          <Input
            value={customRpcRaw}
            onChange={(e) => setCustomRpcRaw(e.target.value)}
            placeholder={t('trade-settings.custom-rpc-placeholder')}
            autoCorrect="false"
            autoComplete="false"
            autoCapitalize="false"
            error={!customRpcValid}
            onBlur={(e) => {
              if (e.relatedTarget instanceof Node) {
                if (!e.relatedTarget.contains(e.target)) {
                  return
                }
              }
              handleCustomRpcSave()
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                e.stopPropagation()
                handleCustomRpcSave()
              }
            }}
          />
        </ExpandableSelectCard>
      </Routes>
    </>
  )
}

const Routes = styled.div`
  display: grid;
  gap: 6px;
`

const SavedBadge = styled(motion.div).attrs({
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
})`
  color: var(--up-9);
  font-size: 11px;
  font-weight: 500;
`

const SubLabel = styled.div`
  color: var(--text-9);
  font-size: 11px;
  font-weight: 500;
  padding-block: 8px 4px;
`

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 4px;
`

const validateRpcUrl = (url: string) => {
  try {
    const parsedUrl = new URL(url)
    const isProtocolValid =
      parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:'
    const isHostValid = parsedUrl.host.length > 0
    return isProtocolValid && isHostValid
  } catch {
    return false
  }
}

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 6px;
`

const Label = styled.div`
  color: var(--text-11);

  font-size: 11px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
`
